import * as fromAppData from '../../appData';
import getRootLayoutItem from 'nucleus-widgets/utils/layout/getRootLayoutItem';
import Logger from '@cvent/nucleus-logging';

const LOG = new Logger('eventWidget/registrationProcessNavigation');

function getRegistrationPaths(state) {
  return state.registrationPaths || {};
}

/**
 * Gets the registration path Id under which the given widget falls
 */
export function getRegistrationPathIdForWidget(state, widgetId) {
  const { website } = state;
  const { layoutItems, pages } = website;

  const widgetLayoutItem = layoutItems[widgetId];

  const rootItem = getRootLayoutItem(layoutItems, widgetLayoutItem);
  const parentPage = Object.values(pages).find((page) => page.rootLayoutItemIds.includes(rootItem.id));
  return parentPage
    ? getRegistrationPathIdForPage(state.website.pluginData.registrationProcessNavigation, parentPage.id)
    : undefined;
}

/**
 * Gets the registration path id that contains a given page
 */
export function getRegistrationPathIdForPage(state, pageId) {
  // eslint-disable-next-line complexity
  const foundRegPath = Object.entries(getRegistrationPaths(state)).find(([_id, regPath]) => {
    const { pageIds = [], postRegPageIds = [], registrationCancellationPageIds = [],
      registrationDeclinePageIds = [], eventWaitlistPageIds = [],
      registrationPendingApprovalPageIds = [], registrationApprovalDeniedPageIds = [],
      guestRegistrationPageIds = [], postRegistrationPaymentPageIds = [] } = regPath;
    return (pageIds || []).find(id => id === pageId)
        || (postRegPageIds || []).find(id => id === pageId)
        || (registrationDeclinePageIds || []).find(id => id === pageId)
        || (registrationCancellationPageIds || []).find(id => id === pageId)
        || (eventWaitlistPageIds || []).find(id => id === pageId)
        || (registrationPendingApprovalPageIds || []).find(id => id === pageId)
        || (registrationApprovalDeniedPageIds || []).find(id => id === pageId)
        || (guestRegistrationPageIds || []).find(id => id === pageId)
        || (postRegistrationPaymentPageIds || []).find(id => id === pageId);
  });
  return foundRegPath ? foundRegPath[0] : undefined;
}

/**
 * Gets the contact field for the contact field widget id passed
 */
export function getContactFieldForWidget(state, registrationFieldPageType, fieldId, widgetId) {
  return fromAppData.getContactField(state.appData, getRegistrationPathIdForWidget(state, widgetId),
    registrationFieldPageType, fieldId);
}

/**
 * Look through website JSON blob to find out which pages the given widget is on
 * then checks if any of the pages are on given path
 * @param {JSON} website - website snapshot, includes layoutItems, pages, and some others
 * @param {string} widgetType - widget type keyword (declared in widgetFactory)
 * @param {string} regPathId - id to regPath to check if widget is present
 * @returns {boolean} - true if present on given path
 */
export function isWidgetOnPath(website, widgetType, regPathId) {
  if (!widgetType || typeof widgetType !== 'string' || !regPathId || !website) {
    return false;
  }
  const allContainingAncestorWidgetIds = Object.values(website.layoutItems)
    .reduce((nonOrphanedWidgetRootIds, widget) => {
      if (widgetType && widget.widgetType === widgetType) {
        try {
          const rootLayoutItemId = getRootLayoutItem(website.layoutItems, widget).id;
          nonOrphanedWidgetRootIds.push(rootLayoutItemId);
        } catch (e) {
          LOG.error(`Widget of widgetType:${widget.widgetType} with widgetId:${widget.id} is orphaned)`);
        }
      }
      return nonOrphanedWidgetRootIds;
    }, []);
  const allPagesWidgetIsPresent = Object.keys(website.pages).filter(pageId =>
    website.pages[pageId].rootLayoutItemIds.some(id => allContainingAncestorWidgetIds.includes(id)));

  // eslint-disable-next-line complexity
  const foundWidgetOnPath = allPagesWidgetIsPresent.find(pageId => {
    const { pageIds = [], postRegPageIds = [], registrationCancellationPageIds = [],
      registrationDeclinePageIds = [], eventWaitlistPageIds = [],
      registrationPendingApprovalPageIds = [], registrationApprovalDeniedPageIds = [],
      guestRegistrationPageIds = [] } = website.pluginData.registrationProcessNavigation.registrationPaths[regPathId];
    return (pageIds || []).find(id => id === pageId)
        || (postRegPageIds || []).find(id => id === pageId)
        || (registrationDeclinePageIds || []).find(id => id === pageId)
        || (registrationCancellationPageIds || []).find(id => id === pageId)
        || (eventWaitlistPageIds || []).find(id => id === pageId)
        || (registrationPendingApprovalPageIds || []).find(id => id === pageId)
        || (registrationApprovalDeniedPageIds || []).find(id => id === pageId)
        || (guestRegistrationPageIds || []).find(id => id === pageId);
  });
  return !!foundWidgetOnPath;
}
